/**
 * list_flows tool - List Power Automate flows in an environment
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { Flow } from "../api/types.js";
import { logger } from "../utils/logger.js";
import { validateODataFilter } from "../utils/security.js";

export const listFlowsInputSchema = z.object({
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
  search: z
    .string()
    .optional()
    .describe("Search flows by display name (case-insensitive substring match). Applied client-side after fetching."),
  filter: z
    .string()
    .optional()
    .describe("OData filter expression (e.g., \"properties/state eq 'Started'\")"),
  top: z
    .number()
    .min(1)
    .max(250)
    .optional()
    .describe("Maximum number of results to return (default 50, max 250)"),
  scope: z
    .enum(["owned", "shared", "all"])
    .optional()
    .default("all")
    .describe(
      "Filter by ownership: 'owned' = only flows you created, 'shared' = only flows shared with you, 'all' = both (default).",
    ),
  includeOwner: z
    .boolean()
    .optional()
    .default(true)
    .describe("Include owner/creator name in the output (default true)."),
  skipToken: z
    .string()
    .optional()
    .describe("Opaque pagination token from a previous list_flows page (nextSkipToken in structured output)."),
  fetchAll: z
    .boolean()
    .optional()
    .default(false)
    .describe("Follow pagination server-side and return all flows (hard cap: 20 pages). Default false — single page."),
});

export type ListFlowsInput = z.infer<typeof listFlowsInputSchema>;

export const listFlowsTool = {
  name: "list_flows",
  description:
    "List Power Automate flows in an environment. Returns flow names, IDs, state (Started/Stopped), last modified dates, and owners. Use scope='shared' to find flows shared with you, scope='owned' for your flows only. Use this to discover what flows exist before getting details or making changes.",
  inputSchema: {
    type: "object" as const,
    properties: {
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
      search: {
        type: "string",
        description: "Search flows by display name (case-insensitive substring match).",
      },
      filter: {
        type: "string",
        description: "OData filter expression (e.g., \"properties/state eq 'Started'\")",
      },
      top: {
        type: "number",
        description: "Maximum number of results to return (default 50, max 250)",
        minimum: 1,
        maximum: 250,
      },
      scope: {
        type: "string",
        enum: ["owned", "shared", "all"],
        description:
          "Filter by ownership: 'owned' = only flows you created, 'shared' = only flows shared with you, 'all' = both (default).",
        default: "all",
      },
      includeOwner: {
        type: "boolean",
        description: "Include owner/creator name in the output (default true).",
        default: true,
      },
      skipToken: {
        type: "string",
        description: "Opaque pagination token from a previous list_flows page (nextSkipToken in structured output).",
      },
      fetchAll: {
        type: "boolean",
        description: "Follow pagination server-side and return all flows (hard cap: 20 pages). Default false — single page.",
        default: false,
      },
    },
  },
};

export interface ListFlowsResult {
  id: string;
  displayName: string;
  state: string;
  createdTime: string;
  lastModifiedTime: string;
  triggerType: string;
  ownerDisplayName?: string;
  ownerObjectId?: string;
  isOwned?: boolean;
}

/** Hard cap on server-side pagination (#11) — 20 pages × top(≤250) = up to 5000 flows. */
const FETCH_ALL_PAGE_CAP = 20;

/** Pull the opaque `$skiptoken` out of an OData nextLink so we can request the next page. */
function extractSkipToken(nextLink: string): string | undefined {
  try {
    return new URL(nextLink).searchParams.get("$skiptoken") ?? undefined;
  } catch {
    return undefined;
  }
}

export async function handleListFlows(
  api: FlowManagementApi,
  input: unknown
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  const parsed = listFlowsInputSchema.parse(input);

  logger.info({ input: parsed }, "Executing list_flows");

  // Validate filter to prevent OData injection
  let safeFilter: string | undefined;
  if (parsed.filter) {
    try {
      safeFilter = validateODataFilter(parsed.filter);
    } catch (err) {
      return {
        text: `Invalid filter: ${err instanceof Error ? err.message : "Filter contains disallowed patterns"}`,
      };
    }
  }

  // Fetch one page (default), or follow nextLink server-side when fetchAll is set (#11).
  let flows: Flow[] = [];
  let skipToken = parsed.skipToken;
  let nextLink: string | undefined;
  let pagesFetched = 0;
  do {
    const result = await api.listFlows({
      environment: parsed.environment,
      filter: safeFilter,
      top: parsed.top ?? 50,
      skipToken,
    });
    flows = flows.concat(result.flows);
    pagesFetched += 1;
    nextLink = result.nextLink;
    skipToken = nextLink ? extractSkipToken(nextLink) : undefined;
  } while (parsed.fetchAll && skipToken && pagesFetched < FETCH_ALL_PAGE_CAP);

  const hasMore = !!nextLink && !!skipToken;
  const truncated = parsed.fetchAll && hasMore; // fetchAll hit the page cap with pages remaining

  // Client-side name search (OData API doesn't support contains/substringof for flows)
  if (parsed.search) {
    const needle = parsed.search.toLowerCase();
    flows = flows.filter(f => f.properties.displayName.toLowerCase().includes(needle));
  }

  // Client-side scope filter — owned vs shared.
  // We compare flow.properties.creator.id to the current user's OID extracted from the access token.
  let currentUserOid: string | undefined;
  if (parsed.scope !== "all" || parsed.includeOwner) {
    currentUserOid = await api.getCurrentUserOid();
    if (parsed.scope !== "all" && !currentUserOid) {
      logger.warn("list_flows: could not derive current user OID; scope filter will not apply");
    }
  }

  if (parsed.scope !== "all" && currentUserOid) {
    flows = flows.filter((f) => {
      const creatorId = f.properties.creator?.id;
      if (!creatorId) return false;
      const isOwned = creatorId === currentUserOid;
      return parsed.scope === "owned" ? isOwned : !isOwned;
    });
  }

  if (flows.length === 0) {
    const text =
      parsed.scope === "shared"
        ? "No flows shared with you in this environment."
        : parsed.scope === "owned"
          ? "No flows you own in this environment."
          : "No flows found";
    return { text, structured: { count: 0, flows: [], nextSkipToken: null, pagesFetched, truncated: false } };
  }

  const scopeLabel =
    parsed.scope === "owned" ? "Owned" : parsed.scope === "shared" ? "Shared with you" : "Flows";
  const lines = [`${scopeLabel} (${flows.length}):`];
  for (const flow of flows) {
    const state = flow.properties.state === "Started" ? "▶" : "⏹";
    const modified = new Date(flow.properties.lastModifiedTime).toLocaleDateString();
    let line = `  ${state} ${flow.properties.displayName} (${flow.name}) - ${modified}`;
    if (parsed.includeOwner) {
      const creator = flow.properties.creator;
      const ownerName = creator?.displayName ?? creator?.email;
      const isOwnedTag =
        currentUserOid && creator?.id === currentUserOid ? " [owned]" : creator ? " [shared]" : "";
      if (ownerName) {
        line += ` — ${ownerName}${isOwnedTag}`;
      }
    }
    lines.push(line);
  }

  if (truncated) {
    lines.push(
      `\n(Stopped at the ${FETCH_ALL_PAGE_CAP}-page cap — more flows remain. Call again with skipToken: "${skipToken}")`
    );
  } else if (hasMore) {
    lines.push(`\n(More flows available — call again with skipToken: "${skipToken}")`);
  } else if (nextLink) {
    // nextLink present but no extractable $skiptoken — shouldn't happen, stay honest.
    lines.push(`\n(More flows available but the pagination token could not be extracted)`);
  }

  // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
  const structured: Record<string, unknown> = {
    count: flows.length,
    flows: flows.map((f) => ({
      id: f.name,
      displayName: f.properties.displayName,
      state: f.properties.state,
      createdTime: f.properties.createdTime,
      lastModifiedTime: f.properties.lastModifiedTime,
      ...(parsed.includeOwner && f.properties.creator
        ? {
            owner: {
              id: f.properties.creator.id,
              displayName: f.properties.creator.displayName,
              email: f.properties.creator.email,
            },
            ...(currentUserOid ? { isOwned: f.properties.creator.id === currentUserOid } : {}),
          }
        : {}),
    })),
    nextSkipToken: hasMore ? skipToken : null,
    pagesFetched,
    truncated,
  };

  return { text: lines.join("\n"), structured };
}
